import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  W3Token,
  W3MfaAssociateResponse,
  W3MfaVerifyResponse,
  Document,
  DocumentUpload,
  DocumentProcessingStatus,
  DocumentPreview,
  Analysis,
  AnalysisRequest,
  AnalysisStatusResponse,
  Report,
  PaginatedResponse,
  PaginationParams,
  FileNode,
  FolderCreate,
  ChatMessage,
  ChatSession,
  ChatJobAccepted,
  ChatJobStatus,
  RecipeJobAccepted,
  RecipeJobStatus,
  Artifact,
  McpStatus,
  McpTool,
  PropertyValuation,
  UnderwritingContext,
  LegalSkill,
  QuotaStatus,
  ProductUsageCategory,
  TaseCompany,
  TaseCompanySearchResult,
  TaseLiveFiling,
  CompanySummary,
  CompanyDetail,
  CompanyReportDetail,
  CompanyReportCreated,
  CorpusRunSummary,
  CorpusValue,
  CompletionCase,
  CompletionItem,
  CompletionFinancialStatus,
  CompletionTargetedJob,
} from '../types';

class ApiService {
  private api: AxiosInstance;

  /** Expose the authenticated axios instance for direct use by hooks. */
  getApi(): AxiosInstance {
    return this.api;
  }

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:8000/api/v1'),
      timeout: 120000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Let the browser set multipart boundaries for FormData uploads.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Don't clear tokens for auth endpoints — let the login page
          // handle its own errors. Only auto-logout for protected API calls.
          const url = error.config?.url || '';
          const isAuthEndpoint = url.includes('/auth/w3-login') || url.includes('/auth/login') || url.includes('/auth/register');
          if (!isAuthEndpoint) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('w3_org_id');
            // Mark this error as "handled by redirect" so callers don't show
            // duplicate error messages while the page is navigating away.
            (error as any)._redirecting = true;
            if (window.location.pathname !== '/login') {
              window.location.replace('/login');
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // W3 Auth
  /**
   * Login endpoint that handles every step of the W3/Cognito flow:
   *   • Initial credentials  → { email, password }
   *   • MFA challenge reply  → { email, session, mfaCode }
   *   • First-login pwd set  → { email, session, newPassword }
   *
   * On full success the response contains tokens; otherwise it contains
   * { challenge, session, message } and the caller drives the next step.
   */
  async w3Login(payload: {
    email: string;
    password?: string;
    session?: string;
    mfaCode?: string;
    mfaType?: string;
    mfaSelection?: string;
    newPassword?: string;
  }): Promise<W3Token> {
    // Translate camelCase frontend → snake_case backend
    const body: Record<string, unknown> = { email: payload.email };
    if (payload.password) body.password = payload.password;
    if (payload.session) body.session = payload.session;
    if (payload.mfaCode) {
      body.mfa_code = payload.mfaCode;
      // Echo the challenge's method (TOTP / email / SMS); default to TOTP.
      body.mfa_type = payload.mfaType || 'SOFTWARE_TOKEN_MFA';
    }
    if (payload.mfaSelection) body.mfa_selection = payload.mfaSelection;
    if (payload.newPassword) body.new_password = payload.newPassword;
    const response: AxiosResponse<W3Token> = await this.api.post('/auth/w3-login', body);
    return response.data;
  }

  /** Begin TOTP enrollment — returns secret + otpauth URI for QR rendering. */
  async w3MfaAssociate(payload: { email: string; session: string }): Promise<W3MfaAssociateResponse> {
    const response: AxiosResponse<W3MfaAssociateResponse> = await this.api.post(
      '/auth/w3-mfa/associate',
      payload
    );
    return response.data;
  }

  /** Confirm TOTP enrollment by submitting the first 6-digit code. */
  async w3MfaVerify(payload: { session: string; userCode: string }): Promise<W3MfaVerifyResponse> {
    const response: AxiosResponse<W3MfaVerifyResponse> = await this.api.post(
      '/auth/w3-mfa/verify',
      { session: payload.session, user_code: payload.userCode }
    );
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/auth/me');
    return response.data;
  }

  // Document endpoints
  async uploadDocument(file: File, projectId?: string): Promise<DocumentUpload> {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) formData.append('project_id', projectId);

    const response: AxiosResponse<DocumentUpload> = await this.api.post('/documents/upload', formData, {
      headers: {
        // Explicitly unset JSON header to avoid 422 on FastAPI multipart parsing.
        'Content-Type': undefined,
      },
    });
    return response.data;
  }

  async getDocuments(params: PaginationParams): Promise<PaginatedResponse<Document>> {
    const response: AxiosResponse<PaginatedResponse<Document>> = await this.api.get('/documents', { params });
    return response.data;
  }

  async getDocument(documentId: number): Promise<Document> {
    const response: AxiosResponse<Document> = await this.api.get(`/documents/${documentId}`);
    return response.data;
  }

  async getDocumentStatus(documentId: number): Promise<DocumentProcessingStatus> {
    const response: AxiosResponse<DocumentProcessingStatus> = await this.api.get(`/documents/${documentId}/status`);
    return response.data;
  }

  async deleteDocument(documentId: number): Promise<void> {
    await this.api.delete(`/documents/${documentId}`);
  }

  async reprocessDocument(
    documentId: number,
    opts?: { force?: boolean; force_ocr?: boolean; engine?: 'smart' | 'tesseract' | 'layout' | 'mineru' },
  ): Promise<DocumentProcessingStatus> {
    const params: Record<string, string | boolean> = {};
    if (opts?.force) params.force = true;
    if (opts?.force_ocr) params.force_ocr = true;
    if (opts?.engine) params.engine = opts.engine;
    const response: AxiosResponse<DocumentProcessingStatus> = await this.api.post(
      `/documents/${documentId}/reprocess`,
      null,
      { params },
    );
    return response.data;
  }

  async rescanDocumentPages(
    documentId: number,
    body: { pages: number[]; engine: 'vision' | 'smart' | 'tesseract' | 'layout'; focus_prompt?: string },
  ): Promise<{
    success: boolean;
    pages_rescanned: number[];
    engine_used: string;
    ai_extraction?: string | null;
    correlation_id?: string | null;
    error?: string | null;
  }> {
    const response: AxiosResponse<any> = await this.api.post(
      `/documents/${documentId}/rescan-pages`,
      body,
    );
    return response.data;
  }

  async getDocumentPreview(documentId: number, charLimit: number = 2000): Promise<DocumentPreview> {
    const response: AxiosResponse<DocumentPreview> = await this.api.get(
      `/documents/${documentId}/preview`,
      { params: { char_limit: charLimit } },
    );
    return response.data;
  }

  async getUserGuide(): Promise<{ markdown: string }> {
    const response: AxiosResponse<{ markdown: string }> = await this.api.get('/meta/user-guide');
    return response.data;
  }

  async getQuota(): Promise<QuotaStatus> {
    const response: AxiosResponse<QuotaStatus> = await this.api.get('/meta/quota');
    return response.data;
  }

  async moveDocument(documentId: number, folderId: string): Promise<void> {
    await this.api.post(`/documents/${documentId}/move`, { folder_id: folderId });
  }

  async archiveDocument(documentId: number): Promise<void> {
    await this.api.post(`/documents/${documentId}/archive`);
  }

  async restoreDocument(documentId: number): Promise<void> {
    await this.api.post(`/documents/${documentId}/restore`);
  }

  // Folder endpoints
  async getFolderTree(opts?: { includeVault?: boolean }): Promise<FileNode[]> {
    const response: AxiosResponse<FileNode[]> = await this.api.get('/folders/tree', {
      params: opts?.includeVault ? { include_vault: true } : undefined,
    });
    return response.data;
  }

  async createFolder(data: FolderCreate): Promise<FileNode> {
    const response: AxiosResponse<FileNode> = await this.api.post('/folders', data);
    return response.data;
  }

  async renameFolder(folderId: string, name: string): Promise<FileNode> {
    const response: AxiosResponse<FileNode> = await this.api.patch(`/folders/${folderId}`, { name });
    return response.data;
  }

  async deleteFolder(folderId: string): Promise<void> {
    await this.api.delete(`/folders/${folderId}`);
  }

  // Analysis endpoints
  async startAnalysis(analysisRequest: AnalysisRequest): Promise<Analysis> {
    const response: AxiosResponse<Analysis> = await this.api.post('/analysis/start', analysisRequest);
    return response.data;
  }

  async getAnalyses(params: PaginationParams): Promise<PaginatedResponse<Analysis>> {
    const response: AxiosResponse<PaginatedResponse<Analysis>> = await this.api.get('/analysis', { params });
    return response.data;
  }

  async getAnalysis(analysisId: number): Promise<Analysis> {
    const response: AxiosResponse<Analysis> = await this.api.get(`/analysis/${analysisId}`);
    return response.data;
  }

  async getAnalysisStatus(analysisId: number): Promise<AnalysisStatusResponse> {
    const response: AxiosResponse<AnalysisStatusResponse> = await this.api.get(`/analysis/${analysisId}/status`);
    return response.data;
  }

  async deleteAnalysis(analysisId: number): Promise<void> {
    await this.api.delete(`/analysis/${analysisId}`);
  }

  // Report endpoints
  async getReport(analysisId: number): Promise<Report> {
    const response: AxiosResponse<Report> = await this.api.get(`/reports/${analysisId}`);
    return response.data;
  }

  async getReportHtml(analysisId: number): Promise<string> {
    const response: AxiosResponse<string> = await this.api.get(`/reports/${analysisId}/html`, {
      responseType: 'text',
    });
    return response.data;
  }

  async downloadReport(analysisId: number): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/reports/${analysisId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Chat endpoints
  async sendChatMessage(
    content: string,
    fileContext?: string[],
    sessionId?: string,
    tools?: string[],
    recipeId?: string,
    projectId?: string,
    includeRawOcr?: boolean,
    legalSkillId?: string,
    domain?: string,
    usageCategory: ProductUsageCategory = 'chat_agent',
  ): Promise<ChatJobAccepted> {
    const response: AxiosResponse<ChatJobAccepted> = await this.api.post('/chat/send', {
      content,
      file_context: fileContext,
      session_id: sessionId,
      tools,
      recipe_id: recipeId,
      project_id: projectId,
      include_raw_ocr: includeRawOcr || undefined,
      legal_skill_id: legalSkillId,
      domain,
      usage_category: usageCategory,
    }, {
      timeout: 30000,  // enqueue is fast now (was 120000 for the old synchronous call)
    });
    return response.data;
  }

  async getChatJob(jobId: string): Promise<ChatJobStatus> {
    const response: AxiosResponse<ChatJobStatus> = await this.api.get(`/chat/jobs/${jobId}`);
    return response.data;
  }

  async getChatSessions(opts: { archived?: boolean } = {}): Promise<ChatSession[]> {
    const response: AxiosResponse<ChatSession[]> = await this.api.get('/chat/sessions', {
      params: opts.archived ? { archived: 'true' } : undefined,
    });
    return response.data;
  }

  async getChatSessionMessages(sessionId: string): Promise<ChatMessage[]> {
    const response: AxiosResponse<ChatMessage[]> = await this.api.get(`/chat/sessions/${sessionId}`);
    return response.data;
  }

  async archiveChatSession(sessionId: string): Promise<void> {
    await this.api.post(`/chat/sessions/${sessionId}/archive`);
  }

  async unarchiveChatSession(sessionId: string): Promise<void> {
    await this.api.post(`/chat/sessions/${sessionId}/unarchive`);
  }

  // MCP status / tools
  async getMcpStatus(): Promise<McpStatus> {
    const response: AxiosResponse<McpStatus> = await this.api.get('/auth/mcp-status');
    return response.data;
  }

  async getMcpTools(): Promise<McpTool[]> {
    const response: AxiosResponse<McpTool[]> = await this.api.get('/auth/mcp-tools');
    return response.data;
  }

  // Recipe endpoints
  async getRecipes(): Promise<any[]> {
    const response = await this.api.get('/recipes/');
    return response.data;
  }

  async getLegalSkills(): Promise<LegalSkill[]> {
    const response = await this.api.get('/recipes/legal-skills');
    return response.data;
  }

  // TASE (Maya) filings — curated catalog search + server-side import.
  async searchTaseCatalog(q?: string): Promise<TaseCompany[]> {
    const response: AxiosResponse<TaseCompany[]> = await this.api.get('/tase/catalog', {
      params: q ? { q } : {},
    });
    return response.data;
  }

  async searchTaseCompanies(q: string, limit = 8): Promise<TaseCompanySearchResult[]> {
    const response: AxiosResponse<TaseCompanySearchResult[]> = await this.api.get(
      '/tase/companies/search',
      { params: { q, limit } },
    );
    return response.data;
  }

  async getTaseCompanyFinancialReports(
    companyId: number,
    limit = 20,
  ): Promise<TaseLiveFiling[]> {
    const response: AxiosResponse<TaseLiveFiling[]> = await this.api.get(
      `/tase/companies/${companyId}/financial-reports`,
      { params: { limit } },
    );
    return response.data;
  }

  async importTaseFiling(req: {
    url: string;
    company?: string;
    title?: string;
    project_id?: string;
    report_id?: number;
    company_id?: number;
    prefer_structured?: boolean;
  }): Promise<DocumentUpload> {
    // Big filings (300+ pages) take a while to stream from Maya server-side.
    const response: AxiosResponse<DocumentUpload> = await this.api.post('/tase/import', req, {
      timeout: 360_000,
    });
    return response.data;
  }

  // Public-company reports corpus dashboard
  async getTrackedCompanies(q?: string): Promise<CompanySummary[]> {
    const response: AxiosResponse<CompanySummary[]> = await this.api.get('/company-reports/companies', {
      params: q?.trim() ? { q: q.trim() } : undefined,
    });
    return response.data;
  }

  async getTrackedCompany(companyId: string): Promise<CompanyDetail> {
    const response: AxiosResponse<CompanyDetail> = await this.api.get(
      `/company-reports/companies/${companyId}`,
    );
    return response.data;
  }

  async getCompanyReport(reportId: string): Promise<CompanyReportDetail> {
    const response: AxiosResponse<CompanyReportDetail> = await this.api.get(
      `/company-reports/${reportId}`,
    );
    return response.data;
  }

  async createCompanyReport(body: {
    document_id: number;
    company_name: string;
    tase_issuer_id?: string;
    sector?: string;
    analysis_profile?: 'general' | 'real_estate';
    period_year: number;
    period_quarter?: number;
    report_type: 'annual' | 'quarterly';
  }): Promise<CompanyReportCreated> {
    const response: AxiosResponse<CompanyReportCreated> = await this.api.post(
      '/company-reports/',
      body,
    );
    return response.data;
  }

  async startCorpusRun(
    reportId: string,
    body: { difficulty?: 'easy' | 'hard' | 'mixed' | null; spec_version_id?: string } = {},
  ): Promise<Pick<CorpusRunSummary, 'run_id' | 'status' | 'stats' | 'error'>> {
    const response = await this.api.post(`/company-reports/${reportId}/corpus-runs`, {
      difficulty: body.difficulty === undefined ? null : body.difficulty,
      spec_version_id: body.spec_version_id,
    });
    return response.data;
  }

  async getCorpusRun(runId: string): Promise<Pick<CorpusRunSummary, 'run_id' | 'status' | 'stats' | 'error'>> {
    const response = await this.api.get(`/company-reports/corpus-runs/${runId}`);
    return response.data;
  }

  async getCorpusValues(runId: string): Promise<CorpusValue[]> {
    const response: AxiosResponse<CorpusValue[]> = await this.api.get(
      `/company-reports/corpus-runs/${runId}/values`,
    );
    return response.data;
  }

  async openCompletionCase(runId: string): Promise<CompletionCase> {
    const response: AxiosResponse<CompletionCase> = await this.api.post(
      `/company-reports/corpus-runs/${runId}/completion-cases`,
    );
    return response.data;
  }

  async getCompletionCaseForRun(runId: string): Promise<CompletionCase> {
    const response: AxiosResponse<CompletionCase> = await this.api.get(
      `/company-reports/corpus-runs/${runId}/completion-case`,
    );
    return response.data;
  }

  async getCompletionCase(caseId: string): Promise<CompletionCase> {
    const response: AxiosResponse<CompletionCase> = await this.api.get(
      `/company-reports/completion-cases/${caseId}`,
    );
    return response.data;
  }

  async getCompletionItems(caseId: string, gapsOnly = false): Promise<CompletionItem[]> {
    const suffix = gapsOnly ? 'gaps' : 'items';
    const response: AxiosResponse<CompletionItem[]> = await this.api.get(
      `/company-reports/completion-cases/${caseId}/${suffix}`,
    );
    return response.data;
  }

  async addCompletionEvidence(
    caseId: string,
    itemKey: string,
    body: {
      value: string;
      unit?: string;
      quote: string;
      section_title?: string;
      page?: number;
      source_document_id?: number;
      source_type?: string;
      source_url?: string;
      scope?: 'consolidated' | 'solo' | 'contractual' | 'unknown';
    },
  ): Promise<CompletionItem> {
    const response: AxiosResponse<CompletionItem> = await this.api.post(
      `/company-reports/completion-cases/${caseId}/items/${itemKey}/evidence`,
      body,
    );
    return response.data;
  }

  async resolveCompletionItem(
    caseId: string,
    itemKey: string,
    body: {
      action: 'approve_candidate' | 'mark_not_applicable' | 'mark_not_disclosed' | 'mark_external_required' | 'accept_limitation' | 'set_financial_status';
      reason_code: string;
      evidence_id?: string;
      note?: string;
      financial_status?: CompletionFinancialStatus;
    },
  ): Promise<CompletionCase> {
    const response: AxiosResponse<CompletionCase> = await this.api.post(
      `/company-reports/completion-cases/${caseId}/items/${itemKey}/resolve`,
      body,
    );
    return response.data;
  }

  async validateCompletionCase(caseId: string): Promise<CompletionCase> {
    const response: AxiosResponse<CompletionCase> = await this.api.post(
      `/company-reports/completion-cases/${caseId}/validate`,
    );
    return response.data;
  }

  async finalizeCompletionCase(caseId: string): Promise<CompletionCase> {
    const response: AxiosResponse<CompletionCase> = await this.api.post(
      `/company-reports/completion-cases/${caseId}/finalize`,
    );
    return response.data;
  }

  async retryCompletionItem(caseId: string, itemKey: string, chatHint?: string): Promise<CompletionTargetedJob> {
    const response: AxiosResponse<CompletionTargetedJob> = await this.api.post(
      `/company-reports/completion-cases/${caseId}/items/${itemKey}/retry`,
      chatHint ? { chat_hint: chatHint } : undefined,
    );
    return response.data;
  }

  async getCompletionJob(jobId: string): Promise<CompletionTargetedJob> {
    const response: AxiosResponse<CompletionTargetedJob> = await this.api.get(
      `/company-reports/completion-jobs/${jobId}`,
    );
    return response.data;
  }

  // Opinion skills are async (submit + poll) — the long best-of-N generation
  // runs in a backend BackgroundTask so the POST returns immediately and we poll
  // GET /recipes/jobs/{id}. Mirrors the chat job flow (sendChatMessage/getChatJob).
  // userNotes: the chat message typed when firing the skill — guidance and
  // rerun corrections the backend injects into the generation prompt.
  async generateSeniorOpinion(documentId: number, docType?: string, userNotes?: string): Promise<RecipeJobAccepted> {
    const response: AxiosResponse<RecipeJobAccepted> = await this.api.post('/recipes/senior-opinion', {
      document_id: documentId,
      doc_type: docType,
      user_notes: userNotes,
    }, {
      timeout: 30000,  // enqueue is fast; generation runs async (poll getRecipeJob)
    });
    return response.data;
  }

  async generateInternalReview(documentId: number, docType?: string, userNotes?: string): Promise<RecipeJobAccepted> {
    const response: AxiosResponse<RecipeJobAccepted> = await this.api.post('/recipes/internal-review', {
      document_id: documentId,
      doc_type: docType,
      user_notes: userNotes,
    }, {
      timeout: 30000,  // enqueue is fast; generation runs async (poll getRecipeJob)
    });
    return response.data;
  }

  async getRecipeJob(jobId: string): Promise<RecipeJobStatus> {
    const response: AxiosResponse<RecipeJobStatus> = await this.api.get(`/recipes/jobs/${jobId}`);
    return response.data;
  }

  // Chat session deletion
  async deleteChatSession(sessionId: string): Promise<void> {
    await this.api.delete(`/chat/sessions/${sessionId}`);
  }

  // Projects
  async getProjects(): Promise<any[]> {
    const response = await this.api.get('/projects/');
    return response.data;
  }

  async createProject(name: string, description?: string): Promise<any> {
    const response = await this.api.post('/projects/', { name, description });
    return response.data;
  }

  async getProject(projectId: string): Promise<any> {
    const response = await this.api.get(`/projects/${projectId}`);
    return response.data;
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.api.delete(`/projects/${projectId}`);
  }

  async assignDocumentsToProject(projectId: string, documentIds: number[]): Promise<any> {
    const response = await this.api.post(`/projects/${projectId}/documents`, { document_ids: documentIds });
    return response.data;
  }

  async removeDocumentFromProject(projectId: string, docId: number): Promise<void> {
    await this.api.delete(`/projects/${projectId}/documents/${docId}`);
  }

  // Valuation and underwriting tabs
  async getValuations(projectId: string): Promise<PropertyValuation[]> {
    const response: AxiosResponse<PropertyValuation[]> = await this.api.get(`/projects/${projectId}/valuations/`);
    return response.data;
  }

  async createValuation(
    projectId: string,
    body: { document_id: number; requested_credit_ils?: number },
  ): Promise<PropertyValuation> {
    const response: AxiosResponse<PropertyValuation> = await this.api.post(
      `/projects/${projectId}/valuations/`,
      body,
    );
    return response.data;
  }

  async validateValuation(projectId: string, valuationId: string): Promise<PropertyValuation> {
    const response: AxiosResponse<PropertyValuation> = await this.api.post(
      `/projects/${projectId}/valuations/${valuationId}/validate`,
    );
    return response.data;
  }

  async askValuationAgent(
    projectId: string,
    valuationId: string,
    question: string,
  ): Promise<{ answer: string; artifacts: Artifact[]; sources?: Record<string, any> }> {
    const response: AxiosResponse<{ answer: string; artifacts: Artifact[]; sources?: Record<string, any> }> = await this.api.post(
      `/projects/${projectId}/valuations/${valuationId}/ask`,
      { question },
    );
    return response.data;
  }

  async createValuationArtifacts(
    projectId: string,
    valuationId: string,
    opts: { artifact_type?: string } = {},
  ): Promise<{ artifacts: Artifact[] }> {
    const response: AxiosResponse<{ artifacts: Artifact[] }> = await this.api.post(
      `/projects/${projectId}/valuations/${valuationId}/artifacts`,
      { artifact_type: opts.artifact_type || 'summary', export_format: 'json' },
    );
    return response.data;
  }

  async downloadValuationArtifact(
    projectId: string,
    valuationId: string,
    exportFormat: 'pdf' | 'xlsx' | 'csv',
  ): Promise<Blob> {
    try {
      const response = await this.api.post(
        `/projects/${projectId}/valuations/${valuationId}/artifacts`,
        { artifact_type: 'summary', export_format: exportFormat },
        { responseType: 'blob' },
      );
      return response.data;
    } catch (err: any) {
      // When responseType is 'blob', axios returns a Blob for error responses
      // too, which hides {detail: "..."} from caller error-handling. Inflate
      // the blob back into a real error payload so toast.error can show why.
      const blob: Blob | undefined = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          try {
            err.response.data = JSON.parse(text);
          } catch {
            err.response.data = { detail: text || 'Download failed' };
          }
        } catch {
          // ignore inflation error; original err is re-thrown below
        }
      }
      throw err;
    }
  }

  async exportChatReport(args: {
    markdown: string;
    title: string;
    format: 'pdf' | 'docx' | 'html' | 'xlsx';
  }): Promise<Blob> {
    try {
      const response = await this.api.post(
        '/reports/render',
        { markdown: args.markdown, title: args.title, format: args.format },
        { responseType: 'blob' },
      );
      return response.data;
    } catch (err: any) {
      const blob: Blob | undefined = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          try { err.response.data = JSON.parse(text); }
          catch { err.response.data = { detail: text || 'הייצוא נכשל' }; }
        } catch { /* ignore */ }
      }
      throw err;
    }
  }

  async createUnderwritingContext(
    projectId: string,
    body: { document_ids: number[]; valuation_id?: string; purpose?: 'review' | 'chat' | 'verdict' },
  ): Promise<UnderwritingContext> {
    const response: AxiosResponse<UnderwritingContext> = await this.api.post(
      `/projects/${projectId}/underwriting/contexts`,
      body,
    );
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; version: string; environment: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
